import { z } from 'zod';
import { BookingState } from './booking.enums';
import { MessageDeliveryStatus } from './thread.enums';
import { PaginationParamsSchema, PaginationResponseSchema } from '../common/common.schemas';

export const MarketplaceHiringThreadSchema = z.object({
  id: z.uuid(),
  /** Null when the conversation started before a booking existed. */
  bookingId: z.uuid().nullable(),
  hostProfileId: z.uuid(),
  vendorProfileId: z.uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

/**
 * `POST /marketplace/threads` — host or vendor opens (or reuses) a
 * conversation with the other party. No booking required.
 * Pass exactly one of `vendorProfileId` (host starting) or
 * `hostProfileId` (vendor starting).
 */
export const StartMarketplaceHiringThreadRequestSchema = z
  .object({
    vendorProfileId: z.uuid().optional(),
    hostProfileId: z.uuid().optional(),
  })
  .superRefine((data, ctx) => {
    const hasVendor = Boolean(data.vendorProfileId);
    const hasHost = Boolean(data.hostProfileId);
    if (hasVendor === hasHost) {
      ctx.addIssue({
        code: 'custom',
        message: 'Pass exactly one of vendorProfileId or hostProfileId',
        path: ['vendorProfileId'],
      });
    }
  });

/** Original upload name (sanitized server-side). Always present on API responses. */
export const MarketplaceHiringThreadAttachmentFileNameSchema = z.string().trim().min(1).max(255);

export const MarketplaceHiringThreadPendingAttachmentSchema = z.object({
  url: z.string().url(),
  /** Storage key from the upload response — used for pre-send delete. */
  key: z.string().min(1),
  mimeType: z.string().min(1).max(120),
  sizeBytes: z.number().int().positive(),
  fileName: MarketplaceHiringThreadAttachmentFileNameSchema,
});

export const MarketplaceHiringMessageAttachmentSchema = z.object({
  id: z.uuid(),
  url: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  fileName: MarketplaceHiringThreadAttachmentFileNameSchema,
  createdAt: z.string(),
});

export const MarketplaceHiringMessageSchema = z.object({
  id: z.uuid(),
  threadId: z.uuid(),
  senderUserId: z.uuid(),
  body: z.string(),
  sentAt: z.string(),
  deliveryStatus: z.enum(MessageDeliveryStatus),
  readAt: z.string().nullable(),
  attachments: z.array(MarketplaceHiringMessageAttachmentSchema),
});

export const ListMarketplaceHiringThreadMessagesQuerySchema = PaginationParamsSchema;

export const ListMarketplaceHiringThreadMessagesResponseSchema = z.object({
  messages: z.array(MarketplaceHiringMessageSchema),
  ...PaginationResponseSchema.shape,
});

export const GetMarketplaceHiringThreadResponseSchema = z.object({
  thread: MarketplaceHiringThreadSchema,
  messages: z.array(MarketplaceHiringMessageSchema),
  ...PaginationResponseSchema.shape,
});

export const PostMarketplaceHiringThreadMessageRequestSchema = z
  .object({
    /** Optional when at least one attachment is present (attachment-only send). */
    body: z.string().trim().max(5000).optional().default(''),
    attachments: z.array(MarketplaceHiringThreadPendingAttachmentSchema).max(10).optional(),
  })
  .refine((value) => value.body.length > 0 || (value.attachments?.length ?? 0) > 0, {
    message: 'Either a non-empty body or at least one attachment is required',
    path: ['body'],
  });

export const PostMarketplaceHiringThreadMessageResponseSchema = z.object({
  message: MarketplaceHiringMessageSchema,
});

/**
 * Multipart upload for hiring-thread chat (images, PDF, Word, MP4).
 * Prefer `POST /marketplace/threads/:threadId/attachments`.
 * Legacy: `POST /marketplace/bookings/:bookingId/thread/attachments`.
 * FE uploads first, then posts `{ url, key, mimeType, sizeBytes, fileName }`
 * via WS `hiring_thread.message.post` (body optional when attachments present).
 */
export const UploadMarketplaceHiringThreadAttachmentResponseSchema = z.object({
  url: z.url(),
  key: z.string().min(1),
  mimeType: z.string().min(1).max(120),
  sizeBytes: z.number().int().positive(),
  fileName: MarketplaceHiringThreadAttachmentFileNameSchema,
});

export const DeleteMarketplaceHiringThreadPendingAttachmentRequestSchema = z.object({
  key: z.string().min(1),
});

export const DeleteMarketplaceHiringThreadMessageAttachmentResponseSchema = z.object({
  messageId: z.uuid(),
  messageDeleted: z.boolean(),
  message: MarketplaceHiringMessageSchema.nullable(),
});

export const MarkMarketplaceHiringThreadReadRequestSchema = z.object({
  upToMessageId: z.uuid().optional(),
});

export const MarkMarketplaceHiringThreadReadResponseSchema = z.object({
  markedCount: z.number().int().nonnegative(),
});

/** Inbox row for host/vendor — one conversation (booking optional). */
export const MarketplaceHiringThreadInboxItemSchema = z.object({
  threadId: z.uuid(),
  bookingId: z.uuid().nullable(),
  title: z.string(),
  lastMessagePreview: z.string().nullable(),
  lastMessageAt: z.string().nullable(),
  lastSenderUserId: z.uuid().nullable(),
  lastSenderDisplayName: z.string().nullable(),
  unreadCount: z.number().int().nonnegative(),
  bookingState: z.enum(BookingState).nullable(),
  vendorProfileId: z.uuid(),
  vendorDisplayName: z.string(),
  vendorAvatarUrl: z.string().nullable(),
  hostProfileId: z.uuid(),
  hostDisplayName: z.string(),
  hostAvatarUrl: z.string().nullable(),
  counterpartyDisplayName: z.string(),
  counterpartyAvatarUrl: z.string().nullable(),
  counterpartyRole: z.enum(['HOST', 'VENDOR']),
});

export const ListMarketplaceHiringThreadsResponseSchema = z.object({
  items: z.array(MarketplaceHiringThreadInboxItemSchema),
  ...PaginationResponseSchema.shape,
});
